import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AccountSettings = ({ userProfile, onUpdateProfile, onChangePassword }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: userProfile?.firstName,
    lastName: userProfile?.lastName,
    email: userProfile?.email,
    phone: userProfile?.phone,
    dateOfBirth: userProfile?.dateOfBirth
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = () => {
    onUpdateProfile(formData);
    setIsEditing(false);
  };

  const handleSavePassword = () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    onChangePassword(passwordData);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordForm(false);
  };

  return (
    <div className="holographic-card p-6 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold command-text">Account Settings</h2>
        <div className="flex items-center space-x-2">
          <div className="trust-seal p-2 bg-success/10 border border-success/30 rounded-lg">
            <Icon name="Shield" size={16} className="text-success" />
          </div>
          <span className="text-xs text-muted-foreground">Verified Account</span>
        </div>
      </div>
      {/* Profile Information */}
      <div className="space-y-6">
        <div className="border-b border-border pb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Profile Information</h3>
            <Button
              variant={isEditing ? "default" : "outline"}
              size="sm"
              onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
              iconName={isEditing ? "Save" : "Edit"}
              iconPosition="left"
            >
              {isEditing ? 'Save Changes' : 'Edit Profile'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="First Name"
              name="firstName"
              value={formData?.firstName}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
            <Input
              label="Last Name"
              name="lastName"
              value={formData?.lastName}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData?.email}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
            <Input
              label="Phone Number"
              name="phone"
              type="tel"
              value={formData?.phone}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
            <Input
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={formData?.dateOfBirth}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Password Section */}
        <div className="border-b border-border pb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Password & Security</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              iconName="Key"
              iconPosition="left"
            >
              Change Password
            </Button>
          </div>

          {showPasswordForm && (
            <div className="space-y-4 bg-muted/20 p-4 rounded-lg">
              <Input
                label="Current Password"
                name="currentPassword"
                type="password"
                value={passwordData?.currentPassword}
                onChange={handlePasswordChange}
                placeholder="Enter current password"
              />
              <Input
                label="New Password"
                name="newPassword"
                type="password"
                value={passwordData?.newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter new password"
              />
              <Input
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={passwordData?.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Confirm new password"
              />
              <div className="flex space-x-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSavePassword}
                  iconName="Save"
                  iconPosition="left"
                >
                  Update Password
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPasswordForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Notification Preferences */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
          <div className="space-y-3">
            {[
              { id: 'email', label: 'Email Notifications', description: 'Order updates and promotions' },
              { id: 'sms', label: 'SMS Notifications', description: 'Delivery alerts and important updates' },
              { id: 'push', label: 'Push Notifications', description: 'Real-time notifications in browser' },
              { id: 'marketing', label: 'Marketing Communications', description: 'Special offers and new products' }
            ]?.map((pref) => (
              <div key={pref?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                <div>
                  <p className="font-medium">{pref?.label}</p>
                  <p className="text-sm text-muted-foreground">{pref?.description}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;